import React, { useEffect, useState } from "react";

import DocumentTitle from "./DocumentTitle";
import Sidebar from "./Sidebar";
import MobileMenuButton from "./MobileMenuButton";
import TabNavigation from "./common/TabNavigation";

const DashboardLayout = ({
  children,
  title,
  subtitle,
  currentUser,
  navConfig,
  primaryAction,
  secondaryActions = [],
  fillHeight = false,
  hideDesktopHeader = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Mount header actions once — duplicating primaryAction in mobile+desktop
  // headers made dropdown outside-click listeners cancel each other on mousedown.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // fillHeight / hideDesktopHeader: title stays in the mobile header only (e.g. Preview, Changes)
  const showDesktopHeader = !fillHeight && !hideDesktopHeader && Boolean(
    title || subtitle || primaryAction || secondaryActions.length > 0,
  );

  const headerActions = (primaryAction || secondaryActions.length > 0) ? (
    <>
      {secondaryActions.map((action, index) => (
        <React.Fragment key={index}>{action}</React.Fragment>
      ))}
      {primaryAction}
    </>
  ) : null;

  return (
    <>
      <DocumentTitle title={title} />
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <div className="flex h-screen min-w-0 max-w-full overflow-x-hidden bg-theme-main">
        <div className="overflow-visible">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} currentUser={currentUser} />
        </div>

        <div className="flex-1 flex flex-col min-h-0 min-w-0 relative overflow-hidden">
          {/* Mobile header: single row aligned with hamburger */}
          <div className="relative z-20 flex h-14 shrink-0 items-center gap-2 bg-theme-main pl-4 pr-4 sm:pl-6 sm:pr-6 lg:hidden">
            <MobileMenuButton onClick={toggleSidebar} />
            {title && (
              <h1 className="min-w-0 flex-1 truncate text-base font-bold text-theme-text">
                {title}
              </h1>
            )}
            {!title && <div className="flex-1" />}
            {!isDesktop && headerActions && (
              <div className="flex shrink-0 items-center gap-2">
                {headerActions}
              </div>
            )}
          </div>

          {/* Desktop header */}
          {showDesktopHeader && (
            <div className="relative z-20 hidden items-start justify-between bg-theme-main lg:flex">
              <div className="flex min-w-0 flex-1 items-start">
                {(title || subtitle) && (
                  <div className="flex min-w-0 flex-1 flex-col justify-center px-3 pb-4 pt-5 sm:px-5 lg:px-6">
                    {title && (
                      <h1 className="text-[21px] font-bold text-theme-text">
                        {title}
                      </h1>
                    )}
                    {subtitle && (
                      <div className="mt-1 flex items-center gap-2 text-[12.5px] text-theme-secondary-text">
                        {typeof subtitle === "string" ? subtitle : subtitle}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {isDesktop && headerActions && (
                <div className="flex items-center gap-3 px-3 pb-4 pt-5 sm:px-5 lg:px-6">
                  {headerActions}
                </div>
              )}
            </div>
          )}

          {navConfig && navConfig.length > 0 && (
            <div className="z-10 flex-shrink-0 overflow-visible border-b border-theme-secondary bg-theme-main px-3 sm:px-5 lg:px-6">
              <TabNavigation items={navConfig} />
            </div>
          )}

          <main
            className={`flex-1 min-h-0 min-w-0 max-w-full bg-theme-main text-theme-text ${
              fillHeight ? "flex flex-col overflow-hidden" : "overflow-y-auto overflow-x-hidden"
            }`}
          >
            <div
              id="dashboard-main-content"
              className={
                fillHeight
                  ? "flex min-h-0 flex-1 flex-col"
                  : "w-full min-w-0 max-w-full box-border px-3 py-3 sm:px-5 lg:px-6"
              }
            >
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;
